import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

const destroyMap = new WeakMap<any, Subject<void>>();
const decoratedSymbol = Symbol('Destroyable');

type DecoratedClass = Function & { [decoratedSymbol]?: true };

const isDecorated = (klassInstance: { constructor: DecoratedClass }) =>
  !!klassInstance.constructor[decoratedSymbol];
const decorate = (klass: DecoratedClass) => (klass[decoratedSymbol] = true);

export function Destroyable(): ClassDecorator {
  return function (klass) {
    decorate(klass);
    const ngOnDestroy: Function | undefined = klass.prototype.ngOnDestroy;
    klass.prototype.ngOnDestroy = function () {
      ngOnDestroy?.apply(this);
      const subject = destroyMap.get(this);
      if (subject) {
        subject.next();
        subject.complete();
        destroyMap.delete(this);
      }
    };
    return klass;
  };
}

export const takeUntilDestroyed = (context: any) => {
  if (!isDecorated(context)) {
    throw new Error(
      `Class must be decorated with 'Destroyable' decorator in order to use 'takeUntilDestroyed' operator`,
    );
  }

  if (!destroyMap.has(context)) {
    destroyMap.set(context, new Subject<void>());
  }
  return <T>(source: Observable<T>): Observable<T> =>
    source.pipe(takeUntil(destroyMap.get(context)!));
};
